import type { OffsetPaginationSort } from '@lmc/ts-common'
import { getJSON } from 'common/services/api'
import dayjs from 'dayjs'
import { useQuery } from 'react-query'
import type { RangeValue } from 'types/shared'
import { getQueryParams } from 'utils/getQueryParams'

export type FilterValues = {
  rangePicker: RangeValue<dayjs.Dayjs>
  residents?: string[]
  categoryItems?: string[]
  locations?: string[]
  statuses?: string[]
  severities?: string[]
  resultedIn?: string[]
  homes?: string[]
}

type PaginationValues = {
  page?: number
  itemsPerPage?: number
  sort?: OffsetPaginationSort<string>
}

type IncidentsParams = FilterValues & PaginationValues

export const generateApiUrl = (
  filterParams: IncidentsParams,
  type: INCIDENT_TYPE_QUERY
) => {
  const {
    residents,
    rangePicker,
    categoryItems,
    locations,
    statuses,
    severities,
    resultedIn,
    homes,
    page,
    itemsPerPage
  } = filterParams

  const query = {
    [`pagination[page]`]: page,
    [`pagination[itemsPerPage]`]: itemsPerPage
  }

  const queryStr = getQueryParams({
    serviceUsers: residents,
    locations,
    statuses,
    severities,
    items: categoryItems,
    resultedIn,
    homes,
    fromDate: dayjs(rangePicker?.[0]).startOf('day').toISOString(),
    toDate: dayjs(rangePicker?.[1]).endOf('day').toISOString(),
    ...query
  })

  return `/api/list/incident-report/${type}?${queryStr}`
}

type INCIDENT_TYPE_QUERY = 'report-list' | 'dashboard'

export const useIncidentsReportList = (
  filterValues: IncidentsParams,
  type = 'report-list' as INCIDENT_TYPE_QUERY
) => {
  const GET_INCIDENTS_REPORT_LIST = generateApiUrl(filterValues, type)

  const { data, isLoading, error, refetch } = useQuery(
    [
      'list-incidents',
      filterValues.categoryItems,
      filterValues.locations,
      filterValues.rangePicker,
      filterValues.residents,
      filterValues.resultedIn,
      filterValues.severities,
      filterValues.statuses,
      filterValues.homes,
      filterValues.page,
      filterValues.itemsPerPage
    ],
    () =>
      getJSON({
        url: `${GET_INCIDENTS_REPORT_LIST}`
      }),
    { enabled: true }
  )

  return { data, isLoading, error, refetch }
}
